import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Loader } from "@/components/ui/loader";
import { Note } from "@/components/ui/note";
import type { LabelField, LabelSettings } from "../types";
import { calculateBarcodeWidthForDPI } from "../utils/barcodeUtils";
import {
  cleanupImageUrl,
  dotsToInches,
  dpiToDpmm,
  generateLabelPreview,
} from "../utils/labelaryApi";
import { calculateTextDimensions } from "../utils/textUtils";
import { generateZPL } from "../utils/zplGenerator";

interface LabelPreviewProps {
  settings: LabelSettings;
  fields: LabelField[];
}

export function LabelPreview({ settings, fields }: LabelPreviewProps) {
  const [previewState, setPreviewState] = useState<{
    loading: boolean;
    imageUrl?: string;
    error?: string;
    warnings?: string[];
  }>({ loading: false });

  const zplCode = generateZPL(settings, fields);

  // Generate preview when ZPL changes
  useEffect(() => {
    if (!zplCode || fields.length === 0) {
      setPreviewState({ loading: false });
      return;
    }

    const generatePreview = async () => {
      setPreviewState({ loading: true });

      try {
        const options = {
          dpmm: dpiToDpmm(settings.dpi),
          width: dotsToInches(settings.width, settings.dpi),
          height: dotsToInches(settings.height, settings.dpi),
        };

        const result = await generateLabelPreview(zplCode, options);

        if (result.success) {
          setPreviewState({
            loading: false,
            imageUrl: result.imageUrl,
            warnings: result.warnings,
          });
        } else {
          setPreviewState({
            loading: false,
            error: result.error,
          });
        }
      } catch (error) {
        setPreviewState({
          loading: false,
          error: error instanceof Error ? error.message : "Unknown error",
        });
      }
    };

    // Debounce the preview generation
    const timeoutId = setTimeout(generatePreview, 500);
    return () => clearTimeout(timeoutId);
  }, [zplCode, settings.dpi, settings.width, settings.height, fields.length]);

  // Cleanup image URL on unmount
  useEffect(() => {
    return () => {
      if (previewState.imageUrl) {
        cleanupImageUrl(previewState.imageUrl);
      }
    };
  }, [previewState.imageUrl]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(zplCode);
    } catch (err) {
      console.error("Failed to copy ZPL to clipboard:", err);
    }
  };

  return (
    <div className="space-y-4">
      <Heading level={3}>Label Preview</Heading>

      {/* Labelary Preview */}
      <div className="space-y-2">
        <span className="text-sm font-medium">Rendered Preview:</span>
        <div className="border rounded-md p-4 bg-white min-h-[200px] flex items-center justify-center">
          {previewState.loading ? (
            <div className="flex items-center space-x-2">
              <Loader />
              <span className="text-sm text-muted-foreground">
                Generating preview...
              </span>
            </div>
          ) : previewState.error ? (
            <Note intent="danger" className="w-full">
              <div className="text-sm">
                <strong>Preview Error:</strong> {previewState.error}
              </div>
            </Note>
          ) : previewState.imageUrl ? (
            <div className="space-y-2">
              <img
                src={previewState.imageUrl}
                alt="Label preview"
                className="max-w-full h-auto border border-gray-200 rounded"
                style={{ imageRendering: "pixelated" }}
              />
              {previewState.warnings && previewState.warnings.length > 0 && (
                <Note intent="warning" className="text-xs">
                  <div>
                    <strong>ZPL Warnings:</strong>
                    <ul className="mt-1 list-disc list-inside">
                      {previewState.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </Note>
              )}
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center text-muted-foreground">
              <p>Add fields to see preview</p>
            </div>
          ) : (
            <div className="text-center text-muted-foreground">
              <p>Preview will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* ZPL Code Section */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">Generated ZPL Code:</span>
          <Button variant="outline" size="sm" onPress={copyToClipboard}>
            Copy ZPL
          </Button>
        </div>

        <div className="bg-gray-100 p-3 rounded-md">
          <pre className="text-xs font-mono whitespace-pre-wrap break-all">
            {zplCode}
          </pre>
        </div>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium">Label Info:</span>
        <div className="text-xs text-muted-foreground space-y-1">
          <div>
            Dimensions: {settings.width} × {settings.height} dots
          </div>
          <div>DPI: {settings.dpi}</div>
          <div>Fields: {fields.length}</div>
          <div>
            Physical size: {(settings.width / settings.dpi).toFixed(2)}" ×{" "}
            {(settings.height / settings.dpi).toFixed(2)}"
          </div>
          <div>
            Margins: L{settings.marginLeft || 8}, T{settings.marginTop || 8}, R
            {settings.marginRight || 8}, B{settings.marginBottom || 8}
          </div>
          <div>
            Printable area:{" "}
            {settings.width -
              (settings.marginLeft || 8) -
              (settings.marginRight || 8)}{" "}
            ×{" "}
            {settings.height -
              (settings.marginTop || 8) -
              (settings.marginBottom || 8)}{" "}
            dots
          </div>
          {(fields.some((f) => f.type === "barcode") ||
            fields.some((f) => f.type === "text")) && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="text-xs font-medium mb-1">Field Analysis:</div>
              {fields.map((field) => {
                if (field.type === "barcode") {
                  const calc = calculateBarcodeWidthForDPI(
                    field.content,
                    settings.dpi,
                    0.0075, // min X dimension
                    field.barcodeHeight || 50,
                    field.showText ?? true,
                    12, // font size
                  );
                  return (
                    <div
                      key={field.id}
                      className="text-xs text-muted-foreground"
                    >
                      Barcode "{field.content}": {calc.widthInDots}×
                      {calc.heightInDots} dots (Set {calc.codeSet},{" "}
                      {calc.characterCount} chars, text: {calc.textHeight}px)
                    </div>
                  );
                }
                if (field.type === "text") {
                  const textCalc = calculateTextDimensions(
                    field.content,
                    field.fontSize || 20,
                    field.fontFamily || "0",
                    field.width,
                  );
                  return (
                    <div
                      key={field.id}
                      className="text-xs text-muted-foreground"
                    >
                      Text "{field.content}": {field.width}×
                      {textCalc.heightInDots} dots ({textCalc.charactersPerLine}{" "}
                      chars/line, {textCalc.numberOfLines} lines)
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
